import { Component, OnInit } from '@angular/core';
import {
	ActivatedRoute,
	NavigationEnd,
	NavigationStart,
	Router,
} from '@angular/router';
import { fade } from '@core/animations/animations';
import { Matter, MatterFileType } from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { FileCreationSelectors } from '@state/global/file_creation';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';
import { Step } from 'src/app/shared/atomic/molecules/step-progress-nav/step-progress-nav.component';
import { ICON } from '@core/util/icon';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { TestHooks } from './create.hooks';
import { BUTTON_PRESETS } from 'src/app/ui/button/button.component';

export enum CreationStepStates {
	APS = 0,
	COMMISSION = 1,
	SUMMARY = 2,
}

interface CreateStep extends Step {
	name: CreationStepStates;
}

const stepDefaults: { [key in CreationStepStates]: CreateStep } = {
	[CreationStepStates.APS]: {
		subTitle: 'Add Details from APS',
		title: 'APS intake',
		name: CreationStepStates.APS,
	},
	[CreationStepStates.COMMISSION]: {
		subTitle: 'Add Details from Commission Statement(s)',
		title: 'Commission intake',
		name: CreationStepStates.COMMISSION,
	},
	[CreationStepStates.SUMMARY]: {
		hideSkip: true,
		subTitle: 'Review File Details',
		title: 'Summary',
		name: CreationStepStates.SUMMARY,
	},
};

@UntilDestroy()
@Component({
	selector: 'app-create-matter',
	templateUrl: './create.component.html',
	styleUrls: ['./create.component.scss'],
	animations: [fade()],
	standalone: false,
})
export class CreateMatterComponent implements OnInit {
	testHooks = TestHooks;
	@ViewSelectSnapshot(SelectedMatter.matterFileType) fileType: MatterFileType;
	@ViewSelectSnapshot(SelectedMatter.matterName) name: string;
	@ViewSelectSnapshot(SelectedMatter.matterNumber) number: string; // yep... the variable number is a string...
	@SelectSnapshot(SelectedMatter.getSelected) selectedMatter: Matter;
	@Select(FileCreationSelectors.getShowCommissionStep)
	showCommissionStep$: Observable<boolean>;
	/**
	 * When we transition from /create/:matterId to /matter/:matterId, we want to keep the matter state,
	 * otherwise we will fetch it again for no reason (Thus will just take more time)
	 * @see {@link services/closer-web/src/app/app-routing.module.ts#272}
	 * @see {@link services/closer-web/src/app/app-routing.module.ts#536}
	 */
	private shouldDestroyMatterStateId = true;
	public matterId: string;
	public ICON = ICON;
	public BUTTON_PRESETS = BUTTON_PRESETS;
	public state = CreationStepStates.APS;
	public states = CreationStepStates;
	public showCommissionStep = true;
	public showSkip$ = new BehaviorSubject<boolean>(true);
	public steps: CreateStep[] = [
		{ ...stepDefaults[CreationStepStates.APS] },
		{ ...stepDefaults[CreationStepStates.COMMISSION] },
		{ ...stepDefaults[CreationStepStates.SUMMARY] },
	];

	constructor(
		private activatedRoute: ActivatedRoute,
		private matterDispatch: MatterDispatch,
		private navService: NavigationService,
		private router: Router,
	) {}

	ngOnInit(): void {
		this.activatedRoute.paramMap
			.pipe(
				untilDestroyed(this),
				map((params) => params.get('matterId')),
				distinctUntilChanged(),
			)
			.subscribe((matterId) => {
				this.matterDispatch.Get(matterId);
				this.matterId = matterId;
			});

		this.router.events
			.pipe(
				untilDestroyed(this),
				filter(
					// If there is a redirect from here and it doesn't contain the matterId, we should destroy the matter state
					(e) => e instanceof NavigationStart && !e.url.includes(this.matterId),
				),
			)
			.subscribe(() => {
				this.matterDispatch.Get(null);
			});

		this.router.events
			.pipe(
				untilDestroyed(this),
				filter((e) => e instanceof NavigationEnd),
				startWith(this.router),
				map(() => this.router.url),
				map(() => this.activatedRoute),
			)
			.subscribe((res) => {
				this.state = res?.firstChild?.snapshot?.data?.step;
				// toggle the skip button if the step declares it
				this.showSkip$.next(!stepDefaults[this.state]?.hideSkip);
			});

		this.showCommissionStep$
			.pipe(untilDestroyed(this))
			.subscribe((_showCommissionStep) => {
				this.showCommissionStep = _showCommissionStep;
				if (_showCommissionStep) {
					this.steps = [
						{ ...stepDefaults[CreationStepStates.APS] },
						{ ...stepDefaults[CreationStepStates.COMMISSION] },
						{ ...stepDefaults[CreationStepStates.SUMMARY] },
					];
				} else {
					this.steps = [
						{ ...stepDefaults[CreationStepStates.APS] },
						{ ...stepDefaults[CreationStepStates.SUMMARY] },
					];
				}
			});
	}

	skip(): void {
		this.goToMatter();
	}

	continue(): void {
		switch (this.state) {
			case CreationStepStates.APS:
				if (this.showCommissionStep) {
					this.navService.goToCreateMatter_Commission(
						this.matterId,
						this.fileType,
					);
				} else {
					this.navService.goToCreateMatter_Summary(this.matterId);
				}
				break;
			case CreationStepStates.COMMISSION:
				this.navService.goToCreateMatter_Summary(this.matterId);
				break;
			case CreationStepStates.SUMMARY:
				this.goToMatter();
				break;
		}
	}

	back(): void {
		switch (this.state) {
			case CreationStepStates.COMMISSION:
				this.navService.goToCreateMatter_APS(this.matterId);
				break;
			case CreationStepStates.SUMMARY:
				if (this.showCommissionStep) {
					this.navService.goToCreateMatter_Commission(
						this.matterId,
						this.fileType,
					);
				} else {
					this.navService.goToCreateMatter_APS(this.matterId);
				}
				break;
		}
	}

	stepClicked = (index: number): void => {
		switch (this.steps[index].name) {
			case 0:
				this.navService.goToCreateMatter_APS(this.matterId);
				break;
			case 1:
				this.navService.goToCreateMatter_Commission(
					this.matterId,
					this.fileType,
				);
				break;
			case 2:
				this.navService.goToCreateMatter_Summary(this.matterId);
				break;
		}
	};

	private goToMatter(): void {
		this.navService.goToMatter(this.matterId);
	}
}
